import { cn, Divider, Stack } from 'component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactLabel, PlotReportFactLabelNameEnum, ValueClass } from '@/api/rest/resources/types/fact';
// TODO: MVP-5260 - Bring Confidence Interval back to charts
// import { RenderConfidenceInterval } from './ConfidenceInterval';
// import { useConfidenceIntervalLegendItem } from './hooks/useConfidenceIntervalLegendItem';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotGroupFilterEndYear } from '@/hooks/usePlotGroupFilterEndYear';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { hasLabel } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { printMonthYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Area, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { CustomEstimationDot } from './components/CustomEstimationDot';
import { ChartLegendBar } from './components/LegendBar';
import {
  ChartTooltip,
  // ChartTooltipConfidenceInterval,TODO: MVP-5260 - Bring Confidence Interval back to charts
  ChartTooltipDot,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useEstimatedDataLegendItem } from './hooks/useEstimatedDataLegendItem';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { ChartDataItem, extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { ChartProps, FactValue } from './types';
import {
  applyBenchmarksToChartData,
  getBenchmarkLabel,
  getChartDataFromGraphFact,
  getDataDefaultDomain,
  getXAxisDomainForHistoricGraph,
  hasInterpolatedValues,
  minMaxAxisDomain,
  sortChartData,
} from './utils';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const waterColors = getColorForIndicator(INDICATOR.WATER);
const soilMoistureColors = getColorForIndicator(INDICATOR.SOIL_MOISTURE);

const chartColors = {
  whc: waterColors,
  sm: soilMoistureColors,
  confidenceInterval: {
    fill: waterColors.confidenceInterval,
  },
  estimatedData: {
    stroke: waterColors.estimatedData,
  },
} as const;

const totalFactNames = {
  soil_moisture: 'sm',
  water_holding_capacity: 'whc',
  below_wilting_point: 'wp',
  water_fc_total_benchmark_low: 'whc_benchmark_low',
  water_fc_total_benchmark_moderate: 'whc_benchmark_moderate',
  water_fc_total_benchmark_high: 'whc_benchmark_high',
};

const perHaFactNames = {
  soil_moisture_per_ha: 'sm',
  water_holding_capacity_per_ha: 'whc',
  below_wilting_point_per_ha: 'wp',
  water_fc_per_ha_benchmark_low: 'whc_benchmark_low',
  water_fc_per_ha_benchmark_moderate: 'whc_benchmark_moderate',
  water_fc_per_ha_benchmark_high: 'whc_benchmark_high',
};

const zoomMultipliers = {
  min: 0,
  max: 1.2,
};

const intrapolateNullValues = (data: ChartDataItem[]) => {
  const interpolatedData = structuredClone(data);
  const whcYearlyValues: Record<string, FactValue | null> = {};
  const wpYearlyValues: Record<string, FactValue | null> = {};

  Object.entries(interpolatedData).forEach(([, currentData]) => {
    whcYearlyValues[new Date(currentData.label).getFullYear().toString()] = currentData.whc ?? null;
    wpYearlyValues[new Date(currentData.label).getFullYear().toString()] = currentData.wp ?? null;
  });
  Object.entries(interpolatedData).forEach(([date, currentData]) => {
    /**
     * Science spec -
     * whc is produced as 1 value per year, constant throughout the year, sm can have multiple values in a year
     * we use this whc value to fill in whc for all sm datapoints of that year that do not have a coninciding whc datapoint
     * so that the tooltip does not show "No data" for whc since whc is null for those dates
     * but as shown in the graph the step chart for whc would indicate a corresponding whc value
     */
    const defaultWhcValue = currentData.whc ?? whcYearlyValues[new Date(currentData.label).getFullYear().toString()];
    const defaultWpValue = currentData.wp ?? wpYearlyValues[new Date(currentData.label).getFullYear().toString()];

    const hasInterpolatedLabels = hasLabel(
      { [PlotReportFactLabelNameEnum.value_class]: ValueClass.interpolated },
      currentData?.whc?.labels ?? [],
    );

    interpolatedData[Number(date)] = {
      ...interpolatedData[Number(date)],
      whc: {
        ...(defaultWhcValue ?? null),
        hasInterpolatedLabels,
      },
      wp: defaultWpValue ?? null,
    };
  });
  return interpolatedData;
};

// TODO MVP-8607: reduce complexity and re-enable check
export const WaterHistoricalChart: FC<
  HTMLAttributes<HTMLDivElement> &
    ChartProps & {
      customBenchmark?: CustomBenchmarkSuccessResponse | null;
    }
> = ({ analysisType, data, benchmarks, customBenchmark, className, showTooltip, zoom, setZoom, ...delegated }) => {
  const { t } = useTranslation();
  const filtersStartYear = usePlotGroupFilterStartYear();
  const limitMaxYear = usePlotGroupFilterEndYear();

  const styles = cn('flex w-full flex-col', className);
  const unit = analysisType === 'total' ? formatUnit(UnitEnum['m^3']) : formatUnit(UnitEnum['m^3/ha']);

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, zoomMultipliers.min, zoomMultipliers.max);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);

  const factNames = analysisType === 'total' ? totalFactNames : perHaFactNames;
  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());
  const intrapolatedData = intrapolateNullValues(sortedChartData);

  const firstTick = intrapolatedData.at(0);
  const lastTick = intrapolatedData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className={cn(styles, 'h-full justify-center')} {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = getXAxisDomainForHistoricGraph(firstTick.label, lastTick.label, filtersStartYear, limitMaxYear);
  const xAxisProps = getXAxisProps(xTicksDomain);

  const dataWithWp = extrapolateReferenceLinesChartData(xTicksDomain, intrapolatedData, 'wp');
  const chartData = applyBenchmarksToChartData(
    dataWithWp,
    customBenchmark,
    xTicksDomain,
    { low: 'whc_benchmark_low', moderate: 'whc_benchmark_moderate', high: 'whc_benchmark_high' },
    extrapolateReferenceLinesChartData,
  );

  const hasInterpolated = hasInterpolatedValues(chartData, 'whc');
  const showSoilMoisture = chartData.some((dataSet) => !!dataSet?.sm);

  const hasLowData = chartData.some((point) => !!point.whc_benchmark_low);
  const hasModerateData = chartData.some((point) => !!point.whc_benchmark_moderate);
  const hasHighData = chartData.some((point) => !!point.whc_benchmark_high);
  const hasWpData = chartData.some((point) => !!point.wp);

  const yAxisDomainFromButtons = getChartZoomedDomain({
    data: data.filter((f) => f.name !== 'precipitation'), // precipitation data is not displayed in the graph
    zoom,
    defaultDomain: minMaxAxisDomain(zoomMultipliers.min, zoomMultipliers.max),
  });

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          whc: {
            // eslint-disable-next-line sonarjs/no-duplicate-string
            label: t('global.analysis.waterHoldingCapacity'),
            color: chartColors.whc.stroke,
          },
          ...(showSoilMoisture && {
            sm: {
              // eslint-disable-next-line sonarjs/no-duplicate-string
              label: t('global.analysis.soilMoisture'),
              color: chartColors.sm.stroke,
            },
          }),
        }}
      >
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id='whc' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={chartColors.whc.fill} stopOpacity={1} />
              <stop offset='100%' stopColor={chartColors.whc.fill} stopOpacity={0} />
            </linearGradient>
          </defs>
          {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
          {/* {RenderConfidenceInterval({
            dataKey: 'whc.confidence_interval',
            type: 'stepBefore',
            fill: chartColors.confidenceInterval.fill,
            id: 'whc-historical-confidence-interval',
          })} */}
          <Area
            type='monotone'
            dataKey='sm.value'
            stroke='var(--color-sm)'
            strokeWidth={1}
            fill='none'
            activeDot={ChartTooltipDot}
            connectNulls
            id='sm-historical-area'
          />
          <Area
            type='stepBefore'
            stroke='var(--color-whc)'
            strokeWidth={2}
            fill='url(#whc)'
            dataKey='whc.value'
            connectNulls
            activeDot={ChartTooltipDot}
            dot={(props) => <CustomEstimationDot {...props} color={chartColors.estimatedData.stroke} />}
            id='whc-historical-area'
          />
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_low.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasLowData, t('global.benchmark.low'), customBenchmark?.name),
            chartData,
            id: 'whc-historical-benchmark-low',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_moderate.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasModerateData, t('global.benchmark.moderate'), customBenchmark?.name),
            chartData,
            id: 'whc-historical-benchmark-moderate',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'whc_benchmark_high.value',
            type: 'monotone',
            label: getBenchmarkLabel(hasHighData, t('global.benchmark.high'), customBenchmark?.name),
            chartData,
            id: 'whc-historical-benchmark-high',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: 'wp.value',
            type: 'stepBefore',
            label: hasWpData ? t('global.analysis.wiltingPoint') : null,
            chartData,
            id: 'wp-historical-line',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='whc-historical-x-axis' />
          <Chart.YAxis domain={yAxisDomain} allowDataOverflow={true} className='whc-historical-y-axis'>
            <Chart.Label>{unit}</Chart.Label>
          </Chart.YAxis>
          {showTooltip && (
            <Chart.Tooltip
              content={(props) => (
                <CustomTooltip
                  {...props}
                  unit={unit as UnitEnum}
                  benchmarks={benchmarks}
                  showSoilMoisture={showSoilMoisture}
                />
              )}
            />
          )}
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar showEstimate={hasInterpolated} showSoilMoisture={showSoilMoisture} />
    </>
  );
};

const CustomLegendBar = ({ showEstimate, showSoilMoisture }: { showEstimate: boolean; showSoilMoisture: boolean }) => {
  const { t } = useTranslation();
  // TODO: MVP-5260 - Bring Confidence Interval back to charts
  // const confidenceIntervalLegendItem = useConfidenceIntervalLegendItem({
  //   confidenceIntervalColor: chartColors.confidenceInterval.fill,
  // });

  const estimatedDataLegendItem = useEstimatedDataLegendItem({
    estimatedDataColor: chartColors.estimatedData.stroke ?? '',
  });

  const soilMoistureLegendItem = [
    {
      label: t('global.analysis.soilMoisture'),
      color: chartColors.sm.stroke,
    },
  ];

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t('global.analysis.waterHoldingCapacity'),
      color: chartColors.whc.fill,
    },
    ...(showEstimate ? estimatedDataLegendItem : []),
    ...(showSoilMoisture ? soilMoistureLegendItem : []),
    // ...confidenceIntervalLegendItem,
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='mt-8 sm:ml-[28px]' data-testid='water-historical-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  unit,
  benchmarks,
  showSoilMoisture,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
TooltipProps<ValueType, NameType> & { unit: UnitEnum; benchmarks?: any[]; showSoilMoisture: boolean }) => {
  const { t } = useTranslation();

  /** sm/whc values are not stored in any particular order in the payload array when multiple graphs are rendered,
   *  the payload just carries non-null values, null values are omitted.
   *  If either whc/sm are null, they will not show up in the payload array
   *  Hence we search for the object for a dataKey match, instead of indexing the array
   *  if they exist, we extract the y value, if not, we assume its null
   */
  const smValue = payload?.find((data) => data.dataKey === 'sm.value');
  const whcValue = payload?.find((data) => data.dataKey === 'whc.value');

  const smDisplay = !isNullish(smValue?.value)
    ? `${getDisplayNumber(smValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');
  const whcDisplay = !isNullish(whcValue?.value)
    ? `${getDisplayNumber(whcValue?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');

  const whcBenchmarkType = useBenchmarkForFact(benchmarks, whcValue?.value);
  const dataPointHasLabel = whcValue && whcValue?.payload?.whc?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? whcValue.payload.whc?.labels?.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;

  const dataLabel = dataPointValueClass?.value as ValueClass;

  if (!active) return null;
  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {whcBenchmarkType && (
        <div className='typography-body2 text-text-secondary'>
          <BenchmarkPill benchmarkType={whcBenchmarkType} />
        </div>
      )}
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>{`${t('global.analysis.waterHoldingCapacity')}`}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{whcDisplay}</ChartTooltipTextPrimary>
      </Stack>
      {/* TODO: MVP-5260 - Bring Confidence Interval back to charts  */}
      {/* <ChartTooltipConfidenceInterval ciPct={whcValue?.payload?.whc?.confidence_interval_pct} /> */}
      {showSoilMoisture && (
        <Stack direction='col' spacing={1.5}>
          <Divider className='my-3' />
          <Stack spacing={8} direction='row' className='justify-between'>
            <ChartTooltipTextSecondary>{printMonthYear(label)}</ChartTooltipTextSecondary>
            {dataLabel && <ValueClassPill title={dataLabel} />}
          </Stack>
          <Stack spacing={8} direction='row' className='justify-between'>
            <ChartTooltipTextPrimary>{`${t('global.analysis.soilMoisture')}`}</ChartTooltipTextPrimary>
            <ChartTooltipTextPrimary>{smDisplay}</ChartTooltipTextPrimary>
          </Stack>
        </Stack>
      )}
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipWaterHistorical };
